<template>
  <div
    class="lead-communication-history-drawer__backdrop"
    @click.self="$emit('close')"
    @wheel.self.prevent
  >
    <aside
      class="lead-communication-history-drawer"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-communication-history-drawer-title"
      @wheel.stop
    >
      <AppModalHeader
        ref="modalHeader"
        design="new"
        title="Full history"
        title-id="lead-communication-history-drawer-title"
        :eyebrow="headerEyebrow"
        @close="$emit('close')"
      />

      <div class="lead-communication-history-drawer__tabs">
        <LeadCommunicationTabs
          design="new"
          :tabs="tabs"
          :active="activeTab"
          @select="$emit('select', $event)"
        />
      </div>

      <main ref="content" class="lead-communication-history-drawer__content">
        <LeadCommunicationTimeline
          v-if="activeTab === 'timeline'"
          :items="flat.results"
          :loading="flat.loading"
          :loading-more="flat.loadingMore"
          :error="flat.error"
          :has-more="flat.hasMore"
          @retry="$emit('retry-flat')"
          @load-more="$emit('load-more-flat')"
        />
        <template v-else>
          <div
            v-if="activeGrouped.loading && !activeGrouped.loaded"
            class="lead-communication-history-drawer__loading"
            aria-label="Loading conversations"
          >
            <span></span>
            <span></span>
          </div>
          <div
            v-else-if="activeGrouped.error && !activeGrouped.groups.results.length"
            class="lead-communication-history-drawer__state"
            role="alert"
          >
            <p>Unable to load conversations.</p>
            <AppButton design="new" theme="empty" type="button" @click.native="$emit('retry-grouped')">
              Retry
            </AppButton>
          </div>
          <div
            v-else-if="activeGrouped.groups.results.length"
            class="lead-communication-history-drawer__groups"
          >
            <LeadConversationGroup
              v-for="item in activeGrouped.groups.results"
              :key="`${activeTab}:${item.group.key}:${groupRefreshKey}`"
              :lead-id="leadId"
              :item="item"
              :pending-ids="pendingIds"
              @edit="$emit('edit', $event)"
              @delete="$emit('delete', $event)"
            />
            <AppButton
              v-if="activeGrouped.groups.hasMore"
              design="new"
              theme="empty"
              class="lead-communication-history-drawer__more"
              type="button"
              :disabled="activeGrouped.loadingMore"
              @click.native="$emit('load-more-grouped')"
            >
              {{ activeGrouped.loadingMore ? 'Loading...' : 'Show older conversations' }}
            </AppButton>
            <p v-if="activeGrouped.error" class="lead-communication-history-drawer__append-error">
              Loading failed.
              <AppButton design="new" theme="empty" type="button" @click.native="$emit('load-more-grouped')">
                Retry
              </AppButton>
            </p>
          </div>
          <p v-else class="lead-communication-history-drawer__empty">No communications in this view</p>
        </template>
      </main>

      <footer class="lead-communication-history-drawer__footer">
        <span>{{ activeTabName }} history</span>
        <AppButton design="new" theme="empty" type="button" @click.native="scrollToTop">
          Back to top
          <span aria-hidden="true">↑</span>
        </AppButton>
      </footer>
    </aside>
  </div>
</template>

<script>
import LeadCommunicationTabs from '@/views/crm/task-tracker/LeadCommunicationTabs.vue'
import LeadCommunicationTimeline from '@/views/crm/task-tracker/LeadCommunicationTimeline.vue'
import LeadConversationGroup from '@/views/crm/task-tracker/LeadConversationGroup.vue'

export default {
  name: 'LeadCommunicationHistoryDrawer',
  components: {
    LeadCommunicationTabs,
    LeadCommunicationTimeline,
    LeadConversationGroup
  },
  props: {
    leadId: {
      type: Number,
      required: true
    },
    tabs: {
      type: Array,
      required: true
    },
    activeTab: {
      type: String,
      required: true
    },
    flat: {
      type: Object,
      required: true
    },
    activeGrouped: {
      type: Object,
      required: true
    },
    groupRefreshKey: {
      type: Number,
      default: 0
    },
    pendingIds: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    activeTabDefinition() {
      return this.tabs.find(tab => tab.code === this.activeTab) || null
    },
    activeTabName() {
      return this.activeTabDefinition ? this.activeTabDefinition.name : 'Communication'
    },
    activeCount() {
      if (!this.activeTabDefinition) return null
      const count = this.activeTabDefinition.count
      return count === null || typeof count === 'undefined' ? null : Number(count)
    },
    headerEyebrow() {
      if (this.activeCount === null) return 'Communication'
      return `Communication · ${this.activeCount} ${this.activeCount === 1 ? 'message' : 'messages'}`
    }
  },
  watch: {
    activeTab() {
      this.$nextTick(this.scrollToTop)
    }
  },
  mounted() {
    document.addEventListener('keyup', this.handleKeyup, true)
    this.$nextTick(() => {
      if (this.$refs.modalHeader) this.$refs.modalHeader.focusClose()
    })
  },
  beforeDestroy() {
    document.removeEventListener('keyup', this.handleKeyup, true)
  },
  methods: {
    handleKeyup(event) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      this.$emit('close')
    },
    scrollToTop() {
      const content = this.$refs.content
      if (content) content.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
}
</script>

<style lang="scss" scoped>
.lead-communication-history-drawer__backdrop {
  position: fixed;
  z-index: 10020;
  inset: 2vh 1vw;
  display: flex;
  justify-content: flex-end;
  overflow: hidden;
  border-radius: $ui-overlay-radius;
  background: rgba(23, 36, 58, .28);
  backdrop-filter: blur(2px);
}

.lead-communication-history-drawer {
  display: grid;
  width: calc(100vw - 40px);
  max-width: 820px;
  min-width: 0;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  background: $ui-surface-soft;
  box-shadow: -18px 0 48px rgba(28, 45, 74, .2);
}

.lead-communication-history-drawer__footer {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $ui-space-20;
  padding: $ui-space-20 $ui-space-24;
  background: $ui-surface;
}

.lead-communication-history-drawer__tabs {
  padding: $ui-space-16 $ui-space-24 $ui-space-12;
  border-bottom: 1px solid $ui-line;
  background: $ui-surface;
}

.lead-communication-history-drawer__content {
  min-height: 0;
  padding: $ui-space-20 $ui-space-24;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #aab5c4 transparent;
  scrollbar-width: thin;
}

.lead-communication-history-drawer__groups {
  display: grid;
  gap: $ui-space-12;
}

.lead-communication-history-drawer__more {
  min-height: $ui-control-height;
  cursor: pointer;
}

.lead-communication-history-drawer__more:disabled {
  cursor: wait;
  opacity: .65;
}

.lead-communication-history-drawer__empty,
.lead-communication-history-drawer__state,
.lead-communication-history-drawer__append-error {
  margin: $ui-space-16 0;
  color: $ui-subtle;
  font-size: $ui-font-size-metadata;
}

.lead-communication-history-drawer__append-error {
  color: $ui-red;
  text-align: center;
}

.lead-communication-history-drawer__loading {
  display: grid;
  gap: $ui-space-12;
}

.lead-communication-history-drawer__loading span {
  height: 140px;
  border-radius: $ui-control-radius;
  background: $ui-line;
}

.lead-communication-history-drawer__footer {
  min-height: 66px;
  border-top: 1px solid $ui-line;
}

.lead-communication-history-drawer__footer > span {
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}

.lead-communication-history-drawer__footer .btn {
  display: inline-flex;
  min-height: $ui-control-height-compact;
  align-items: center;
  gap: $ui-space-8;
}

@media (max-width: 720px) {
  .lead-communication-history-drawer__backdrop {
    inset: 0;
    border-radius: 0;
  }

  .lead-communication-history-drawer {
    width: 100%;
  }

  .lead-communication-history-drawer__footer,
  .lead-communication-history-drawer__tabs,
  .lead-communication-history-drawer__content {
    padding-right: 16px;
    padding-left: 16px;
  }

}
</style>
